import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import {
  WhenContributionsAreMade,
  calculateTapped,
  compoundInterestFormulaCopiedTapped,
  onAnnualContributionsChanged,
  onCompoundIntervalChanged,
  onCurrencyChanged,
  onInitialInvestmentChanged,
  onInterestRateChanged,
  onMonthlyContributionsChanged,
  onPeriodMonthsChanged,
  onPeriodYearsChanged,
  onWhenContributionsAreMadeChanged,
  resultFutureInvestmentValueCopiedTapped,
  resultTotalContributionsCopiedTapped,
  resultTotalInterestCopiedTapped,
  resultTotalPrincipalCopiedTapped,
  selectState,
} from "./CompoundInterestCalculatorSlice";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { ResultCard } from "../../components/result_card/ResultCard";
import { CurrencySelect } from "../../components/currency_select/CurrencySelect";
import { CompoundInterval } from "../../utilities/finance";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { HomeCard } from "../../components/home_card/HomeCard";
import CreditCardRepaymentCalculatorImage from "../../assets/CreditCardRepaymentCalculator.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function CompoundInterestCalculator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("interestCalculator.title")}
      pageSeoTitle={t("interestCalculator.seoTitle")}
      pageDescription={t("interestCalculator.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("interestCalculator.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <CurrencySelect
            label={t("interestCalculator.currencyLabel")}
            value={state.currency}
            onChange={(e) => dispatch(onCurrencyChanged(e.target.value))}
          ></CurrencySelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.initialInvestmentLabel")}
            variant="outlined"
            color="info"
            value={state.initialInvestment}
            onChange={(e) => {
              dispatch(onInitialInvestmentChanged(Number(e.target.value)));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{state.currency}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.interestRateLabel")}
            variant="outlined"
            color="info"
            value={state.interestRatePercentage}
            onChange={(e) => {
              dispatch(onInterestRateChanged(Number(e.target.value)));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>%</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={6}>
          <CustomSelect
            label={t("interestCalculator.compoundIntervalLabel")}
            value={state.compoundInterval}
            onChange={(e) =>
              dispatch(
                onCompoundIntervalChanged(
                  Number(e.target.value) as CompoundInterval
                )
              )
            }
          >
            <MenuItem value={CompoundInterval.daily}>
              {t("interestCalculator.compoundIntervals.daily")}
            </MenuItem>
            <MenuItem value={CompoundInterval.semiWeekly}>
              {t("interestCalculator.compoundIntervals.semiWeekly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.weekly}>
              {t("interestCalculator.compoundIntervals.weekly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.biWeekly}>
              {t("interestCalculator.compoundIntervals.biWeekly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.semiMonthly}>
              {t("interestCalculator.compoundIntervals.semiMonthly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.monthly}>
              {t("interestCalculator.compoundIntervals.monthly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.biMonthly}>
              {t("interestCalculator.compoundIntervals.biMonthly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.quarterly}>
              {t("interestCalculator.compoundIntervals.quarterly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.halfYearly}>
              {t("interestCalculator.compoundIntervals.halfYearly")}
            </MenuItem>
            <MenuItem value={CompoundInterval.yearly}>
              {t("interestCalculator.compoundIntervals.yearly")}
            </MenuItem>
          </CustomSelect>
        </Grid2>
        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.periodYearsLabel")}
            variant="outlined"
            color="info"
            value={state.periodYears}
            onChange={(e) => {
              dispatch(onPeriodYearsChanged(Number(e.target.value)));
            }}
          />
        </Grid2>

        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.periodMonthsLabel")}
            variant="outlined"
            color="info"
            value={state.periodMonths}
            onChange={(e) => {
              dispatch(onPeriodMonthsChanged(Number(e.target.value)));
            }}
          />
        </Grid2>

        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.annualContributionsLabel")}
            variant="outlined"
            color="info"
            value={state.annualContributions}
            onChange={(e) => {
              dispatch(onAnnualContributionsChanged(Number(e.target.value)));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{state.currency}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>

        <Grid2 xs={6}>
          <TextField
            fullWidth
            label={t("interestCalculator.monthlyContributionsLabel")}
            variant="outlined"
            color="info"
            value={state.monthlyContributions}
            onChange={(e) => {
              dispatch(onMonthlyContributionsChanged(Number(e.target.value)));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography>{state.currency}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <Typography>
            {t("interestCalculator.contributionsQuestion")}
          </Typography>

          <ToggleButtonGroup
            color="secondary"
            value={state.whenContributionsAreMade}
            exclusive
            onChange={(
              event: React.MouseEvent<HTMLElement>,
              newValue: string | null
            ) => {
              dispatch(
                onWhenContributionsAreMadeChanged(
                  Number(newValue) as WhenContributionsAreMade
                )
              );
            }}
            aria-label="text alignment"
          >
            <ToggleButton
              value={WhenContributionsAreMade.beginning}
              aria-label="left aligned"
            >
              <Typography>
                {t("interestCalculator.contributionTiming.beginning")}
              </Typography>
            </ToggleButton>
            <ToggleButton
              value={WhenContributionsAreMade.end}
              aria-label="centered"
            >
              <Typography>
                {t("interestCalculator.contributionTiming.end")}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid2>
        <Grid2 xs={12}>
          <CustomButton
            label={t("interestCalculator.calculateButton")}
            onClick={() => {
              dispatch(calculateTapped());
            }}
          />
          <Box sx={{ mb: 2 }} />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultFutureInvestmentValue}`}
            description={t("interestCalculator.results.futureInvestmentValue")}
            symbol={state.currency}
            symbolAtStart={true}
            resultCopied={state.resultFutureInvestmentValueCopied}
            onCopy={() => {
              dispatch(resultFutureInvestmentValueCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultTotalPrincipal}`}
            description={t("interestCalculator.results.totalPrincipal")}
            symbol={state.currency}
            symbolAtStart={true}
            resultCopied={state.resultTotalPrincipalCopied}
            onCopy={() => {
              dispatch(resultTotalPrincipalCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultTotalContributions}`}
            description={t("interestCalculator.results.totalContributions")}
            symbol={state.currency}
            symbolAtStart={true}
            resultCopied={state.resultTotalContributionsCopied}
            onCopy={() => {
              dispatch(resultTotalContributionsCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <ResultCard
            value={`${state.resultTotalInterest}`}
            description={t("interestCalculator.results.totalInterest")}
            symbol={state.currency}
            symbolAtStart={true}
            resultCopied={state.resultTotalInterestCopied}
            onCopy={() => {
              dispatch(resultTotalInterestCopiedTapped());
            }}
          />
        </Grid2>
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h2" sx={{ mt: 5 }}>
            {t("interestCalculator.content.maximizeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3 }}>
            {t("interestCalculator.content.welcomeDescription")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3 }}>
            {t("interestCalculator.content.toolDescription")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("interestCalculator.content.earlyStartDescription")}
          </Typography>
          <ResultCard
            value={`A = P(1 + r/n)ⁿᵗ`}
            description={t(
              "interestCalculator.results.compoundInterestFormula"
            )}
            symbol={""}
            resultCopied={state.compoundInterestFormulaCopied}
            onCopy={() => {
              dispatch(compoundInterestFormulaCopiedTapped());
            }}
          />
          <Typography variant="body1" sx={{ mt: 3 }}>
            {t("interestCalculator.content.formulaDescription")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("interestCalculator.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="creditCardCalculator"
            link="/CreditCardRepaymentCalculator"
            smallCard={true}
            image={CreditCardRepaymentCalculatorImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
